/**
 * Vintage Ribelle - Animation Controller
 * Premium vintage fashion brand
 * Vanilla JS, ES6+, zero dependencies
 *
 * Handles: scroll reveal, text reveal, number counters, parallax,
 *          marquee, magnetic buttons, cursor glow, image reveal,
 *          hero sequencing, line animations, stagger-on-scroll,
 *          and smooth hash scrolling.
 */

class AnimationController {
  constructor() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.revealObserver = null;
    this.lineObserver = null;
    this.staggerObserver = null;
    this.counterObserver = null;
    this.mutationObserver = null;
    this.parallaxElements = [];
    this.parallaxTicking = false;
    this.scrollMotionElements = [];
    this.scrollMotionTicking = false;
    this.scrollMotionBound = false;
    this.marqueeTrack = null;
    this.magneticElements = [];
    this.cursorGlowElements = [];
    this.resizeTicking = false;

    this.init();
  }

  /* ==================================================================
     Utility Functions (static helpers)
     ================================================================== */

  /**
   * Throttle: limit execution to once per `delay` ms.
   */
  throttle(fn, delay = 100) {
    let lastTime = 0;
    let timeoutId = null;

    return function (...args) {
      const now = Date.now();
      const remaining = delay - (now - lastTime);

      if (remaining <= 0) {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        lastTime = now;
        fn.apply(this, args);
      } else if (!timeoutId) {
        timeoutId = setTimeout(() => {
          lastTime = Date.now();
          timeoutId = null;
          fn.apply(this, args);
        }, remaining);
      }
    };
  }

  /**
   * Debounce: delay execution until `delay` ms of inactivity.
   */
  debounce(fn, delay = 250) {
    let timeoutId = null;

    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fn.apply(this, args);
        timeoutId = null;
      }, delay);
    };
  }

  /**
   * RAF-based throttle: ensures fn runs at most once per frame.
   */
  rafThrottle(fn) {
    let ticking = false;

    return function (...args) {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        fn.apply(this, args);
        ticking = false;
      });
    };
  }

  /**
   * Check if viewport is mobile width.
   */
  isMobile(breakpoint = 750) {
    return window.innerWidth < breakpoint;
  }

  /**
   * Check if user prefers reduced motion.
   * Considers both matchMedia AND html class (for custom overrides).
   */
  isReducedMotion() {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const htmlClass = document.documentElement.classList.contains('reduced-motion');
    return mediaQuery || htmlClass;
  }

  /**
   * Check for fine pointer (mouse, not touch).
   */
  prefersFinePointer() {
    return window.matchMedia('(any-pointer: fine)').matches;
  }

  /* ==================================================================
     Initialization
     ================================================================== */

  init() {
    if (this.isReducedMotion()) {
      this.showAllRevealElements();
    } else {
      this.initRevealObserver();
      this.initLineAnimation();
      this.initStaggerOnScroll();
      this.initCounterAnimation();
      this.initParallax();
      this.initScrollMotion();
      this.initMarquee();
      this.initMagneticButtons();
      this.initCursorGlow();
      this.initImageReveal();
      this.initHeroAnimation();
      this.initSmoothScroll();
    }

    this.initMutationObserver();
    this.registerMotionPreferenceListener();
    this.bindResizeHandler();
    this.bindEvents();
  }

  /* ==================================================================
     Reduced Motion
     ================================================================== */

  showAllRevealElements() {
    const reveals = document.querySelectorAll(
      '.vr-reveal, .vr-reveal--up, .vr-reveal--down, .vr-reveal--left, .vr-reveal--right, .vr-reveal--text, .vr-reveal--scale'
    );
    reveals.forEach((el) => {
      el.classList.add('is-visible');
      el.style.transitionDelay = '0s';
    });

    const cascades = document.querySelectorAll('.vr-reveal--cascade');
    cascades.forEach((container) => {
      Array.from(container.children).forEach((child) => {
        child.classList.add('is-visible');
        child.style.transitionDelay = '0s';
      });
      container.classList.add('is-visible');
    });

    const lines = document.querySelectorAll('.vr-line-animate');
    lines.forEach((el) => el.classList.add('is-visible'));

    const staggerLists = document.querySelectorAll('.vr-stagger-list');
    staggerLists.forEach((list) => {
      Array.from(list.children).forEach((child) => {
        child.classList.add('is-visible');
        child.style.transitionDelay = '0s';
      });
    });
  }

  registerMotionPreferenceListener() {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!motionQuery.addEventListener) return;

    motionQuery.addEventListener('change', (e) => {
      this.reducedMotion = e.matches;
      if (e.matches) {
        this.showAllRevealElements();
        this.destroyParallax();
        this.destroyMagneticButtons();
        this.destroyCursorGlow();
        this.teardown();
      } else {
        this.init();
      }
    });
  }

  /* ==================================================================
     MutationObserver (dynamic elements - Shopify sections)
     ================================================================== */

  initMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      let needsReInit = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          // Single reveal elements
          if (
            node.matches &&
            (node.matches('.vr-reveal') ||
              node.matches('.vr-reveal--up') ||
              node.matches('.vr-reveal--down') ||
              node.matches('.vr-reveal--left') ||
              node.matches('.vr-reveal--right') ||
              node.matches('.vr-reveal--text') ||
              node.matches('.vr-reveal--scale'))
          ) {
            if (this.isReducedMotion()) {
              node.classList.add('is-visible');
            } else if (this.revealObserver) {
              this.revealObserver.observe(node);
            }
          }

          // Cascade containers
          if (node.matches && node.matches('.vr-reveal--cascade')) {
            if (this.isReducedMotion()) {
              node.classList.add('is-visible');
              Array.from(node.children).forEach((child) => {
                child.classList.add('is-visible');
                child.style.transitionDelay = '0s';
              });
            } else if (this.revealObserver) {
              this.revealObserver.observe(node);
            }
          }

          // Line animations
          if (node.matches && node.matches('.vr-line-animate')) {
            if (this.isReducedMotion()) {
              node.classList.add('is-visible');
            } else if (this.lineObserver) {
              this.lineObserver.observe(node);
            }
          }

          // Stagger lists
          if (node.matches && node.matches('.vr-stagger-list')) {
            if (!this.isReducedMotion() && this.staggerObserver) {
              this.staggerObserver.observe(node);
            }
          }

          // Counter elements
          if (node.matches && node.matches('[data-counter]')) {
            if (!this.isReducedMotion() && this.counterObserver) {
              this.counterObserver.observe(node);
            }
          }

          // Check children recursively
          if (node.querySelectorAll) {
            const reveals = node.querySelectorAll(
              '.vr-reveal, .vr-reveal--up, .vr-reveal--down, .vr-reveal--left, .vr-reveal--right, .vr-reveal--text, .vr-reveal--scale'
            );
            reveals.forEach((child) => {
              if (this.isReducedMotion()) {
                child.classList.add('is-visible');
              } else if (this.revealObserver) {
                this.revealObserver.observe(child);
              }
            });

            const cascades = node.querySelectorAll('.vr-reveal--cascade');
            cascades.forEach((child) => {
              if (!this.isReducedMotion() && this.revealObserver) {
                this.revealObserver.observe(child);
              }
            });

            const lines = node.querySelectorAll('.vr-line-animate');
            lines.forEach((child) => {
              if (!this.isReducedMotion() && this.lineObserver) {
                this.lineObserver.observe(child);
              }
            });

            const staggers = node.querySelectorAll('.vr-stagger-list');
            staggers.forEach((child) => {
              if (!this.isReducedMotion() && this.staggerObserver) {
                this.staggerObserver.observe(child);
              }
            });

            const counters = node.querySelectorAll('[data-counter]');
            counters.forEach((child) => {
              if (!this.isReducedMotion() && this.counterObserver) {
                this.counterObserver.observe(child);
              }
            });

            const parallaxEls = node.querySelectorAll('[data-parallax]');
            parallaxEls.forEach((el) => {
              if (!this.isReducedMotion()) {
                const disableMobile = el.getAttribute('data-parallax-mobile');
                if (!this.isMobile() || disableMobile !== null) {
                  this.parallaxElements.push(el);
                }
              }
            });

            const magnetics = node.querySelectorAll('.vr-magnetic');
            if (this.prefersFinePointer()) {
              magnetics.forEach((el) => this.bindMagneticElement(el));
            }

            const glows = node.querySelectorAll('[data-cursor-glow]');
            if (this.prefersFinePointer()) {
              glows.forEach((el) => this.bindCursorGlowElement(el));
            }

            const imageReveals = node.querySelectorAll('.vr-image-reveal');
            imageReveals.forEach((el) => this.handleImageReveal(el));

            needsReInit = true;
          }
        });
      });

      if (needsReInit) {
        document.dispatchEvent(new CustomEvent('section:loaded'));
      }
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /* ==================================================================
     1. Scroll Reveal (IntersectionObserver) — ENHANCED
     ================================================================== */

  initRevealObserver() {
    if (this.revealObserver) {
      this.revealObserver.disconnect();
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;

          // Skip elements with .no-animations
          if (el.classList.contains('no-animations')) {
            this.revealObserver.unobserve(el);
            return;
          }

          // Cascade: add .is-visible to parent, CSS handles children stagger
          if (el.classList.contains('vr-reveal--cascade')) {
            const delayAttr = el.getAttribute('data-stagger-delay');
            const baseDelay = parseInt(delayAttr || '80', 10);

            Array.from(el.children).forEach((child, index) => {
              if (
                child.classList.contains('vr-reveal') ||
                child.classList.contains('vr-reveal--up') ||
                child.classList.contains('vr-reveal--down') ||
                child.classList.contains('vr-reveal--left') ||
                child.classList.contains('vr-reveal--right') ||
                child.classList.contains('vr-reveal--text') ||
                child.classList.contains('vr-reveal--scale')
              ) {
                child.style.transitionDelay = `${index * baseDelay}ms`;
                child.classList.add('is-visible');
              }
            });

            el.classList.add('is-visible');
            this.revealObserver.unobserve(el);
            return;
          }

          // Standard reveal
          el.classList.add('is-visible');
          this.revealObserver.unobserve(el);
        });
      },
      {
        threshold: 0.12,
        rootMargin: '60px 0px 60px 0px',
      }
    );

    // Observe all reveal elements
    const reveals = document.querySelectorAll(
      '.vr-reveal, .vr-reveal--up, .vr-reveal--down, .vr-reveal--left, .vr-reveal--right, .vr-reveal--text, .vr-reveal--scale, .vr-reveal--cascade'
    );
    reveals.forEach((el) => this.revealObserver.observe(el));
  }

  /* ==================================================================
     2. Text Reveal Animation — NEW
     ================================================================== */

  initTextReveal() {
    const textReveals = document.querySelectorAll('.vr-reveal--text');
    textReveals.forEach((el) => {
      const delay = parseFloat(el.getAttribute('data-reveal-delay') || '0');
      if (delay > 0) {
        el.style.transitionDelay = `${delay}s`;
      }
    });
  }

  /* ==================================================================
     3. Number Counter — NEW
     ================================================================== */

  initCounterAnimation() {
    if (this.counterObserver) {
      this.counterObserver.disconnect();
    }

    this.counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          if (el.classList.contains('no-animations')) {
            this.counterObserver.unobserve(el);
            return;
          }

          this.animateCounter(el);
          this.counterObserver.unobserve(el);
        });
      },
      {
        threshold: 0.2,
        rootMargin: '40px 0px 40px 0px',
      }
    );

    const counters = document.querySelectorAll('[data-counter]');
    counters.forEach((el) => this.counterObserver.observe(el));
  }

  animateCounter(el) {
    const target = parseInt(el.getAttribute('data-counter'), 10);
    if (isNaN(target)) return;

    const magnitude = Math.abs(target);
    let duration;

    // Duration based on magnitude
    if (magnitude <= 100) duration = 1200;
    else if (magnitude <= 1000) duration = 1600;
    else if (magnitude <= 10000) duration = 2000;
    else duration = 2500;

    const startTime = performance.now();
    const startValue = 0;

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startValue + (target - startValue) * eased);

      el.textContent = current;

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = target;
      }
    };

    requestAnimationFrame(step);
  }

  /* ==================================================================
     4. Parallax — ENHANCED
     ================================================================== */

  initParallax() {
    this.parallaxElements = Array.from(
      document.querySelectorAll('[data-parallax]')
    );

    if (this.parallaxElements.length === 0) return;

    // Filter: disable on mobile UNLESS data-parallax-mobile is set
    if (this.isMobile()) {
      this.parallaxElements = this.parallaxElements.filter((el) => {
        return el.hasAttribute('data-parallax-mobile');
      });
    }

    if (this.parallaxElements.length === 0) return;

    window.addEventListener('scroll', () => this.onParallaxScroll(), { passive: true });
  }

  onParallaxScroll() {
    if (this.parallaxTicking) return;
    this.parallaxTicking = true;

    requestAnimationFrame(() => {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;

      this.parallaxElements.forEach((el) => {
        const speed = parseFloat(el.getAttribute('data-parallax-speed') || '0.3');
        const rect = el.getBoundingClientRect();

        // Only animate if element is near the viewport
        if (rect.bottom > -viewportHeight && rect.top < viewportHeight * 2) {
          const elementCenter = rect.top + rect.height / 2;
          const viewportCenter = viewportHeight / 2;
          const offset = (elementCenter - viewportCenter) * speed;

          el.style.transform = `translate3d(0, ${-offset}px, 0)`;
        }
      });

      this.parallaxTicking = false;
    });
  }

  destroyParallax() {
    this.parallaxElements.forEach((el) => {
      el.style.transform = '';
    });
    this.parallaxElements = [];
  }

  /* ==================================================================
     Scroll Motion
     ================================================================== */

  initScrollMotion() {
    this.scrollMotionElements = Array.from(
      document.querySelectorAll('[data-vr-scroll]')
    );

    if (this.scrollMotionElements.length === 0) return;

    this.onScrollMotion();
    if (!this.scrollMotionBound) {
      window.addEventListener('scroll', () => this.onScrollMotion(), { passive: true });
      this.scrollMotionBound = true;
    }
  }

  onScrollMotion() {
    if (this.scrollMotionTicking) return;
    this.scrollMotionTicking = true;

    requestAnimationFrame(() => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      this.scrollMotionElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -viewportHeight * 0.2 || rect.top > viewportHeight * 1.2) return;

        const total = viewportHeight + rect.height;
        const progress = Math.min(1, Math.max(0, (viewportHeight - rect.top) / total));
        const eased = 1 - Math.pow(1 - progress, 3);
        const amount = eased - 0.5;

        const x = parseFloat(el.getAttribute('data-scroll-x') || '0') * amount;
        const y = parseFloat(el.getAttribute('data-scroll-y') || '0') * amount;
        const rotate = parseFloat(el.getAttribute('data-scroll-rotate') || '0') * amount;
        const scaleBase = parseFloat(el.getAttribute('data-scroll-scale') || '0');
        const scale = 1 + scaleBase * amount;

        el.style.setProperty('--vr-scroll-x', `${x.toFixed(2)}px`);
        el.style.setProperty('--vr-scroll-y', `${y.toFixed(2)}px`);
        el.style.setProperty('--vr-scroll-rotate', `${rotate.toFixed(3)}deg`);
        el.style.setProperty('--vr-scroll-scale', scale.toFixed(4));
      });

      this.scrollMotionTicking = false;
    });
  }

  destroyScrollMotion() {
    this.scrollMotionElements.forEach((el) => {
      el.style.removeProperty('--vr-scroll-x');
      el.style.removeProperty('--vr-scroll-y');
      el.style.removeProperty('--vr-scroll-rotate');
      el.style.removeProperty('--vr-scroll-scale');
    });
    this.scrollMotionElements = [];
  }

  /* ==================================================================
     5. Marquee Animation — ENHANCED
     ================================================================== */

  initMarquee() {
    this.marqueeTrack = document.querySelector('.moving-marquee__track');
    if (!this.marqueeTrack) return;

    // Clone content for seamless loop
    const originalContent = this.marqueeTrack.innerHTML;
    this.marqueeTrack.innerHTML = originalContent + originalContent;

    this.setMarqueeDuration();

    if (this.isReducedMotion()) {
      this.marqueeTrack.style.animationPlayState = 'paused';
    } else {
      this.marqueeTrack.style.animationPlayState = 'running';
    }

    // Pause on hover
    const container = this.marqueeTrack.closest('.moving-marquee');
    if (container) {
      container.addEventListener('mouseenter', () => {
        if (!this.isReducedMotion()) {
          this.marqueeTrack.style.animationPlayState = 'paused';
        }
      });

      container.addEventListener('mouseleave', () => {
        if (!this.isReducedMotion()) {
          this.marqueeTrack.style.animationPlayState = 'running';
        }
      });
    }
  }

  setMarqueeDuration() {
    if (!this.marqueeTrack) return;

    const contentWidth = this.marqueeTrack.scrollWidth / 2;
    if (contentWidth === 0) return;

    const speed = parseInt(
      this.marqueeTrack.getAttribute('data-marquee-speed') || '40',
      10
    );
    const duration = contentWidth / speed;

    this.marqueeTrack.style.animationDuration = `${duration}s`;
  }

  /* ==================================================================
     6. Magnetic Buttons — ENHANCED
     ================================================================== */

  initMagneticButtons() {
    if (!this.prefersFinePointer()) return;

    this.magneticElements = Array.from(
      document.querySelectorAll('.vr-magnetic')
    );

    this.magneticElements.forEach((el) => this.bindMagneticElement(el));
  }

  bindMagneticElement(el) {
    if (el._magneticBound) return;
    el._magneticBound = true;

    const handleMove = (e) => this.handleMagneticMove(e, el);
    const handleLeave = (e) => this.handleMagneticLeave(e, el);

    el.addEventListener('mousemove', handleMove);
    el.addEventListener('mouseleave', handleLeave);

    // Store for cleanup
    el._magneticHandlers = { handleMove, handleLeave };
    this.magneticElements.push(el);
  }

  handleMagneticMove(e, el) {
    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    const maxPull = 8; // max 8px

    // Quadratic falloff: stronger near center
    const distanceFromCenter = Math.sqrt(
      Math.pow(e.clientX - centerX, 2) +
      Math.pow(e.clientY - centerY, 2)
    );
    const maxDistance = Math.max(rect.width, rect.height) / 2;
    const intensity = Math.max(0, 1 - Math.pow(distanceFromCenter / maxDistance, 2));

    const pullX = (mouseX / (rect.width / 2)) * maxPull * intensity * 0.3;
    const pullY = (mouseY / (rect.height / 2)) * maxPull * intensity * 0.3;

    el.style.transform = `translate3d(${pullX}px, ${pullY}px, 0)`;
    el.style.transition = 'transform 0.2s ease-out';
  }

  handleMagneticLeave(e, el) {
    el.style.transform = 'translate3d(0, 0, 0)';
    el.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
  }

  destroyMagneticButtons() {
    this.magneticElements.forEach((el) => {
      el.style.transform = '';
      el.style.transition = '';
      if (el._magneticHandlers) {
        el.removeEventListener('mousemove', el._magneticHandlers.handleMove);
        el.removeEventListener('mouseleave', el._magneticHandlers.handleLeave);
        el._magneticBound = false;
      }
    });
    this.magneticElements = [];
  }

  /* ==================================================================
     7. Cursor Glow Effect — NEW
     ================================================================== */

  initCursorGlow() {
    if (!this.prefersFinePointer()) return;

    this.cursorGlowElements = Array.from(
      document.querySelectorAll('[data-cursor-glow]')
    );

    this.cursorGlowElements.forEach((el) => this.bindCursorGlowElement(el));
  }

  bindCursorGlowElement(el) {
    if (el._cursorGlowBound) return;
    el._cursorGlowBound = true;

    // Create glow element
    const glow = document.createElement('div');
    glow.classList.add('vr-cursor-glow');
    glow.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.3s ease;
      z-index: 0;
    `;

    // Ensure parent has position relative
    const computedPos = window.getComputedStyle(el).position;
    if (computedPos === 'static') {
      el.style.position = 'relative';
    }

    el.style.overflow = 'hidden';
    el.appendChild(glow);

    let ticking = false;
    let lastX = 0;
    let lastY = 0;

    const handleEnter = () => {
      glow.style.opacity = '1';
    };

    const handleMove = (e) => {
      lastX = e.clientX;
      lastY = e.clientY;

      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const x = lastX - rect.left;
        const y = lastY - rect.top;

        glow.style.background = `radial-gradient(circle 300px at ${x}px ${y}px, rgba(201, 169, 110, 0.08), transparent 100%)`;
        ticking = false;
      });
    };

    const handleLeave = () => {
      glow.style.opacity = '0';
    };

    el.addEventListener('mouseenter', handleEnter);
    el.addEventListener('mousemove', handleMove);
    el.addEventListener('mouseleave', handleLeave);

    // Store for cleanup
    el._glowHandlers = { handleEnter, handleMove, handleLeave };
    el._glowElement = glow;
  }

  destroyCursorGlow() {
    this.cursorGlowElements.forEach((el) => {
      if (el._glowElement) {
        el._glowElement.remove();
        el._glowElement = null;
      }
      if (el._glowHandlers) {
        el.removeEventListener('mouseenter', el._glowHandlers.handleEnter);
        el.removeEventListener('mousemove', el._glowHandlers.handleMove);
        el.removeEventListener('mouseleave', el._glowHandlers.handleLeave);
      }
      el._cursorGlowBound = false;
    });
    this.cursorGlowElements = [];
  }

  /* ==================================================================
     8. Image Reveal on Load — NEW
     ================================================================== */

  initImageReveal() {
    const containers = document.querySelectorAll('.vr-image-reveal');
    containers.forEach((el) => this.handleImageReveal(el));
  }

  handleImageReveal(el) {
    if (el.classList.contains('is-loaded')) return;

    const img = el.querySelector('img');
    if (img) {
      if (img.complete && img.naturalWidth > 0) {
        el.classList.add('is-loaded');
      } else {
        img.addEventListener(
          'load',
          () => el.classList.add('is-loaded'),
          { once: true }
        );
        img.addEventListener(
          'error',
          () => el.classList.add('is-loaded'),
          { once: true }
        );
      }
    } else {
      // Background image: check if loaded via computed style
      const bgImage = window.getComputedStyle(el).backgroundImage;
      if (bgImage && bgImage !== 'none') {
        // Create a hidden img to track load
        const urlMatch = bgImage.match(/url\(["']?([^"')]+)["']?\)/);
        if (urlMatch) {
          const testImg = new Image();
          testImg.onload = () => el.classList.add('is-loaded');
          testImg.onerror = () => el.classList.add('is-loaded');
          testImg.src = urlMatch[1];
        } else {
          el.classList.add('is-loaded');
        }
      } else {
        el.classList.add('is-loaded');
      }
    }
  }

  /* ==================================================================
     9. Hero Animation Sequence — ENHANCED
     ================================================================== */

  initHeroAnimation() {
    const hero = document.querySelector('.hero-ribelle');
    if (!hero) return;

    if (hero.classList.contains('hero-loaded')) return;

    // Start ken-burns immediately if hero image exists
    const heroImage = hero.querySelector('.hero-ribelle__image, .hero-ribelle__media img');
    if (heroImage) {
      heroImage.style.animationPlayState = 'running';
    }

    // Stagger order: overline -> title -> decorative-line -> subtitle -> CTAs -> scroll-indicator
    const sequence = [
      '.hero-ribelle__overline',
      '.hero-ribelle__title',
      '.hero-ribelle__decorative-line',
      '.hero-ribelle__subtitle',
      '.hero-ribelle__cta',
      '.hero-ribelle__scroll-indicator',
    ];

    const elements = [];
    sequence.forEach((selector) => {
      const el = hero.querySelector(selector);
      if (el) elements.push(el);
    });

    if (elements.length === 0) {
      hero.classList.add('hero-loaded');
      return;
    }

    const baseDelay = 300;
    const staggerDelay = 120;

    elements.forEach((el, index) => {
      const delay = baseDelay + index * staggerDelay;

      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.style.transition = `opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms, transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        });
      });
    });

    // Mark hero as loaded after all animations
    const totalDuration = baseDelay + elements.length * staggerDelay + 800;
    setTimeout(() => {
      hero.classList.add('hero-loaded');

      elements.forEach((el) => {
        el.style.opacity = '';
        el.style.transform = '';
        el.style.transition = '';
      });
    }, totalDuration);
  }

  /* ==================================================================
     10. Line Animation on Scroll — NEW
     ================================================================== */

  initLineAnimation() {
    if (this.lineObserver) {
      this.lineObserver.disconnect();
    }

    this.lineObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          if (el.classList.contains('no-animations')) {
            this.lineObserver.unobserve(el);
            return;
          }

          el.classList.add('is-visible');
          this.lineObserver.unobserve(el);
        });
      },
      {
        threshold: 0.2,
        rootMargin: '40px 0px 40px 0px',
      }
    );

    const lines = document.querySelectorAll('.vr-line-animate');
    lines.forEach((el) => this.lineObserver.observe(el));
  }

  /* ==================================================================
     11. Stagger on Scroll — NEW
     ================================================================== */

  initStaggerOnScroll() {
    if (this.staggerObserver) {
      this.staggerObserver.disconnect();
    }

    this.staggerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          if (el.classList.contains('no-animations')) {
            this.staggerObserver.unobserve(el);
            return;
          }

          const children = el.children;
          const baseDelay = parseInt(
            el.getAttribute('data-stagger-delay') || '100',
            10
          );

          Array.from(children).forEach((child, index) => {
            if (child.classList.contains('no-animations')) return;
            child.style.transitionDelay = `${index * baseDelay}ms`;
            child.classList.add('is-visible');
          });

          el.classList.add('is-visible');
          this.staggerObserver.unobserve(el);
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px 50px 0px',
      }
    );

    const staggerLists = document.querySelectorAll('.vr-stagger-list');
    staggerLists.forEach((el) => this.staggerObserver.observe(el));
  }

  /* ==================================================================
     12. Smooth Scroll to Hash — ENHANCED
     ================================================================== */

  initSmoothScroll() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href === '#') return;

      const target = document.querySelector(href);
      if (!target) return;

      e.preventDefault();

      this.smoothScrollTo(target);
    });
  }

  smoothScrollTo(target) {
    const header = document.querySelector('.header');
    const headerHeight = header ? header.offsetHeight : 0;
    const offset = headerHeight + 20;

    const targetRect = target.getBoundingClientRect();
    const targetPosition = targetRect.top + window.pageYOffset - offset;

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });
  }

  /* ==================================================================
     Resize Handler
     ================================================================== */

  bindResizeHandler() {
    window.addEventListener(
      'resize',
      this.debounce(() => {
        this.onResize();
      }, 150)
    );
  }

  onResize() {
    // Recalculate marquee duration
    if (this.marqueeTrack) {
      this.setMarqueeDuration();
    }

    // Re-evaluate parallax on mobile/desktop switch
    this.parallaxElements = Array.from(
      document.querySelectorAll('[data-parallax]')
    ).filter((el) => {
      if (this.isMobile()) {
        return el.hasAttribute('data-parallax-mobile');
      }
      return true;
    });

    this.scrollMotionElements = Array.from(
      document.querySelectorAll('[data-vr-scroll]')
    );
    this.onScrollMotion();

    // Re-evaluate magnetic and cursor glow on breakpoint change
    const hasFinePointer = this.prefersFinePointer();
    if (hasFinePointer && this.magneticElements.length === 0) {
      this.initMagneticButtons();
    }
    if (hasFinePointer && this.cursorGlowElements.length === 0) {
      this.initCursorGlow();
    }

    // Dispatch for other modules
    document.dispatchEvent(new CustomEvent('animation:resize'));
  }

  /* ==================================================================
     Global Events
     ================================================================== */

  bindEvents() {
    // Listen for Shopify section load
    document.addEventListener('section:loaded', () => {
      if (!this.isReducedMotion()) {
        this.initRevealObserver();
        this.initLineAnimation();
        this.initStaggerOnScroll();
        this.initCounterAnimation();
        this.initParallax();
        this.initScrollMotion();
        this.initImageReveal();
      }
    });

    // Listen for cart updated to re-check any animation triggers
    document.addEventListener('cart:updated', () => {
      // Re-scan for new reveal elements in cart drawer
      if (!this.isReducedMotion() && this.revealObserver) {
        const reveals = document.querySelectorAll(
          '.vr-reveal, .vr-reveal--up, .vr-reveal--down, .vr-reveal--left, .vr-reveal--right, .vr-reveal--text, .vr-reveal--scale'
        );
        reveals.forEach((el) => {
          if (
            !el.classList.contains('is-visible') &&
            !el.classList.contains('no-animations')
          ) {
            this.revealObserver.observe(el);
          }
        });
      }
    });
  }

  /* ==================================================================
     Teardown / Cleanup
     ================================================================== */

  teardown() {
    if (this.revealObserver) this.revealObserver.disconnect();
    if (this.lineObserver) this.lineObserver.disconnect();
    if (this.staggerObserver) this.staggerObserver.disconnect();
    if (this.counterObserver) this.counterObserver.disconnect();
    if (this.mutationObserver) this.mutationObserver.disconnect();
    this.destroyParallax();
    this.destroyScrollMotion();
    this.destroyMagneticButtons();
    this.destroyCursorGlow();
  }
}

/* ==================================================================
   Bootstrap
   ================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  window.animationController = new AnimationController();
});
